/**
 * The in-progress slot of an own building's upgrade (`UpgradeCard` /
 * `UpgradeActionRow`, `phase="inProgress"`): a bar, the time left, what the
 * wait is about and Cancel, labelled with the refund. Paused under attack the
 * bar is warn-striped and says why. All numbers come from `view/upgradeProgress.ts`.
 */
import {
  clockText,
  type UpgradeProgress as Progress,
  RESUME_SECONDS,
} from '@/game/view/upgradeProgress';
import { Cost } from './Cost';

export function UpgradeProgress({
  progress,
  onCancel,
}: {
  progress: Progress;
  onCancel: () => void;
}) {
  const { fraction, paused, secondsLeft, refund, title } = progress;
  const hasRefund = Object.keys(refund).length > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <div
          role="progressbar"
          aria-label={`Upgrading to ${title}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fraction * 100)}
          className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-fill"
        >
          <div
            className={`h-full rounded-full ${paused ? 'bg-warn bg-[repeating-linear-gradient(135deg,var(--color-warn)_0_5px,color-mix(in_srgb,var(--color-warn)_45%,transparent)_5px_10px)]' : 'bg-accent'}`}
            style={{ width: `${fraction * 100}%` }}
          />
        </div>
        <span className={`font-medium tabular-nums ${paused ? 'text-warn' : 'text-accent'}`}>
          {clockText(secondsLeft)}
        </span>
      </div>
      {paused ? (
        <p className="text-warn">
          Paused: under attack
          <span className="text-fg-subtle"> · resumes {RESUME_SECONDS} s after the last hit</span>
        </p>
      ) : (
        <p>
          {title} <span className="text-fg-muted">· stats apply when done</span>
        </p>
      )}
      <button type="button" onClick={onCancel} className="btn btn-secondary ml-auto text-xs">
        Cancel
        {hasRefund && (
          <>
            {' '}
            · refund <Cost cost={refund} />
          </>
        )}
      </button>
    </div>
  );
}
